import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'; // Needed for Touch functionality of Material Components
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from './layout/layout.module';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { AuthenticationService } from './services/base/authentication.service';
import { ApiService } from './services/base/api.service';
import { SnackbarService } from './services/base/snackbar.service';
import { AddTokenInterceptor } from './interceptors/add-token.interceptor';
import { AddAcceptInterceptor } from './interceptors/add-accept.interceptor';
import { AuthorizationService } from './services/base/authorization.service';
import { FormService } from './services/base/form.service';
import { OrganisationService } from './services/routering/organisation.service';
import { ApiEndpointsService } from './services/base/api-endpoints.service';
import { DepartmentService } from './services/routering/department.service';
import { RoleService } from './services/routering/role.service';
import { UserService } from './services/routering/user.service';
import { FiltersService } from './services/base/filters.service';
import { HeadCategoryService } from './services/routering/head-category.service';
import { AddressBookService } from './services/configuration/address-book.service';
import { EmailTemplateService } from './services/configuration/email-template.service';
import { StandardReactionService } from './services/configuration/standard-reaction.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { KnowledgeBaseService } from './services/knowledge-base/knowledge-base.service';
import { QuillService } from './services/base/quill.service';
import { ReactiveFormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlDutch } from '../@fury/shared/internationalization/mat-paginator/mat-paginator-intl.dutch';
import { CityService } from './services/configuration/city.service';
import { ReportSourceService } from './services/configuration/report-source.service';
import { ReportStatusService } from './services/configuration/report-status.service';
import { ReportService } from './services/reports/report.service';
import { ReportHistoryService } from './services/configuration/report-history.service';
import { ReportPanelService } from './services/reports/report-panel.service';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { LightboxModule } from 'ng-gallery/lightbox';
import { RemovableAttachmentModule } from '../@fury/shared/removable-attachment/removable-attachment.module';
import { ReportPrintService } from './services/reports/report-print.service';
import { PublicKnowledgeBaseService } from './services/knowledge-base/public-knowledge-base.service';
import { FaqService } from './services/knowledge-base/faq.service';
import { AddSocketIdInterceptor } from './interceptors/add-socket-id.interceptor';
import { KnowledgeBaseArticlesService } from './services/knowledge-base/knowledge-base-articles.service';
import { SupportContactDetailsService } from './services/knowledge-base/support-contact-details.service';
import { SubCategoryService } from './services/routering/sub-category.service';
import { StatisticsService } from './services/statistics/statistics.service';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { FuryAlertBoxModule } from '../@fury/shared/alert-box/alert-box.module';
import { LayoutService } from './layout/layout.service';
import { LocalStorageService } from './services/base/local-storage.service';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { DateTimeService } from './services/base/date-time.service';
import { MAT_SELECTSEARCH_DEFAULT_OPTIONS, MatSelectSearchOptions, NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { AnonymizedService } from './services/archive/anonymized.service';
import { ArchiveService } from './services/archive/archive.service';
import { SentEmailsService } from './services/sent-emails/sent-emails.service';
import { SentEmailEventsService } from './services/sent-emails/sent-email-events.service';
import { PendingChangesGuard } from './services/base/pending-changes-guard';
import { UnsavedChangesDialogComponent } from '../@fury/shared/dialog/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ReportWebhookService } from './services/reports/report-webhook.service';
import { HolidayService } from './services/configuration/holiday.service';
import { ReportPriorityService } from './services/configuration/report-priority.service';
import { BlacklistService } from './services/configuration/blacklist.service';
import { WasteOrderStatusService } from './services/configuration/waste-order-status.service';
import { WasteOrderTypeService } from './services/configuration/waste-order-type.service';
import { WasteOrdersService } from './services/waste-orders/waste-orders.service';
import { WasteOrderPrintService } from './services/waste-orders/waste-order-print.service';
import { WasteOrderPanelService } from './services/waste-orders/waste-order-panel.service';
import { FurySharedDateModule } from '../@fury/fury-shared-date.module';
import { ReporterService } from './services/reporters/reporter.service';
import { NgxMaskModule } from 'ngx-mask';
import { BroadcastService } from './services/base/broadcast.service';
import { PostalcodeGroupService } from './services/configuration/postalcode-group.service';

import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import * as moment from 'moment';
import { ContractorAgreementService } from './services/configuration/contractor-agreement.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { InstantErrorStateMatcher } from '../@fury/utils/instant-error-state-matcher';
import { MsalBroadcastService, MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { AccountInfo, BrowserCacheLocation, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { Observable, of } from 'rxjs';
import { MsalAuthService } from './services/base/msal-auth.service';
import { User } from './interfaces/routering/user';
import { version } from '../environments/version';

registerLocaleData(localeNl);
moment.locale('nl');

@NgModule({
  imports: [
    // Angular Core Module // Don't remove!
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ReactiveFormsModule,

    // Fury Core Modules
    AppRoutingModule,
    FurySharedDateModule.forRoot(),

    MsalModule.forRoot(new PublicClientApplication({
        auth: {
          clientId: environment.msalConfig.clientId,
          authority: environment.msalConfig.authority,
          redirectUri: environment.msalConfig.redirectUri,
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
          storeAuthStateInCookie: false,
          /*
                  },
                  system: {
                    loggerOptions: {
                      loggerCallback: (logLevel, message, piiEnabled) => {
                        console.log('MSAL Logging: ', message);
                      },
                      piiLoggingEnabled: false
                    }
          */
        }
      }),
      {
        interactionType: InteractionType.Redirect,
        authRequest: {scopes: ['User.Read']}
      },
      {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
          ['https://graph.microsoft.com/v1.0/me', ['User.Read']],
//          [environment.api_endpoint + 'auth/*', null],
//          [environment.api_endpoint, ['api://' + environment.msalConfig.clientId + '/access_as_user']],
        ])
      }),

    // Layout Module (Sidenav, Toolbar, Quickpanel, Content)
    LayoutModule,
    RemovableAttachmentModule,

    LightboxModule.withConfig({
      panelClass: 'fullscreen'
    }),

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true, // environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    QuillModule.forRoot({
      suppressGlobalRegisterWarning: true,
    }),
    FuryAlertBoxModule,
    NgxMatSelectSearchModule,
    MatDialogModule,
    MatButtonModule,

    NgxMaskModule.forRoot(),
  ],
  declarations: [
    AppComponent,
    UnsavedChangesDialogComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        console.log(version + environment.appVersionAddon);
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AuthenticationService, MsalService, MsalAuthService],
      multi: true
    },
    {
      provide: ErrorStateMatcher,
      useClass: InstantErrorStateMatcher
    },
//    {provide: LOCALE_ID, useValue: 'nl'},
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill'
      } as MatFormFieldDefaultOptions
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom'
      } as MatSnackBarConfig
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '0',
        disablePagination: true,
        preserveContent: true,
      }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddAcceptInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddSocketIdInterceptor,
      multi: true
    },
    /*
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true
        },
    */
    {
      provide: MatPaginatorIntl,
      useValue: MatPaginatorIntlDutch()
    },
    {
      provide: MAT_SELECTSEARCH_DEFAULT_OPTIONS,
      useValue: <MatSelectSearchOptions>{
        preventHomeEndKeyPropagation: true,
        clearSearchInput: false,
        noEntriesFoundLabel: 'Geen data',
        placeholderLabel: 'Zoeken ...'
      }
    },
    ApiEndpointsService,
    ApiService,
    AuthenticationService,
    AuthorizationService,
    BroadcastService,
    FiltersService,
    QuillService,
    FormService,

    MsalService,
    MsalBroadcastService,
    MsalAuthService,

    OrganisationService,
    DepartmentService,
    LocalStorageService,
    SnackbarService,
    RoleService,
    UserService,
    HeadCategoryService,
    SubCategoryService,
    AddressBookService,
    EmailTemplateService,
    StandardReactionService,
    FaqService,
    KnowledgeBaseService,
    KnowledgeBaseArticlesService,
    SupportContactDetailsService,
    PublicKnowledgeBaseService,
    CityService,
    ReportSourceService,
    ReportStatusService,
    ReportHistoryService,
    ReportService,
    ReportPanelService,
    ReportPrintService,
    StatisticsService,
    LayoutService,
    DateTimeService,
    AnonymizedService,
    ArchiveService,
    SentEmailsService,
    SentEmailEventsService,
    PendingChangesGuard,
    ReportWebhookService,
    HolidayService,
    ReportPriorityService,
    BlacklistService,
    WasteOrderStatusService,
    WasteOrderTypeService,
    WasteOrdersService,
    WasteOrderPrintService,
    WasteOrderPanelService,
    ReporterService,
    PostalcodeGroupService,
    ContractorAgreementService,
  ]
})

export class AppModule {
}

export function initializeApp(
  authenticationService: AuthenticationService,
  msalService: MsalService,
  msalAuthService: MsalAuthService
): () => Observable<any> {
  return (): Observable<boolean> => {
    msalAuthService.initialize();

    if (!authenticationService.isImpersonating()) {
      if (!environment.production || this.user && this.user.role === 'super-admin') {
        console.log('Auth: check if any MSAL accounts are available');
      }
      const accounts: AccountInfo[] = msalService.instance.getAllAccounts();
      if (!environment.production || this.user && this.user.role === 'super-admin') {
        console.log('Auth: MSAL found ' + accounts.length + ' accounts');
      }
      if (accounts.length) {
        if (!environment.production || this.user && this.user.role === 'super-admin') {
          console.log('Auth: set MSAL account active and validate');
        }
        msalService.instance.setActiveAccount(accounts[0]);
        return msalAuthService.acquireTokenSilent();
      }
    }

    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('Auth: check if an account is available');
    }
    const localUser: User = authenticationService.getUserFromLocalStorage();
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('Auth: found ', localUser);
    }
    if (localUser) {
      if (!environment.production || this.user && this.user.role === 'super-admin') {
        console.log('Auth: validate an account');
      }
      return authenticationService.getUserAccount();
    }

    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('Auth: no accounts available, user is not logged in.');
    }
    return of(null);
  };
}
